import "./SystemSettingsTableWrapperComponent.scss";
import {ITableColumn} from "../../../shared/models/table.model";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import commonService from "../../../shared/services/common.service";
import {APIConfig, ImageConfig} from "../../../constants";
import ButtonComponent from "../../../shared/components/core/button/ButtonComponent";
import * as Yup from "yup";
import CheckboxComponent from "../../../shared/components/core/checkbox/CheckboxComponent";
import _ from "lodash";
import {Field, FieldArray, FieldProps, Form, Formik, FormikHelpers} from "formik";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from '@mui/icons-material/Delete';
import TableWrapperV2Component
    from "../../../shared/components/table-components/table-wrapper-v2/TableWrapperV2Component";
import DrawerComponent from "../../../shared/components/drawer/drawerComponent";
import FormikInputComponent from "../../../shared/components/core/formik-input/FormikInputComponent";
import HeadingComponent from "../../../shared/components/heading/headingComponent";
import {CommonService} from "../../../shared/services";
import SearchComponent from "../../../shared/components/search/SearchComponent";
import {useDispatch} from "react-redux";
import {
    getEquipmentTypeList,
    getEthnicityData,
    getFacilityTypes, getInsuranceCompanyData,
    getLabListLite, getLocationListLite, getMaritalStatusList, getPayorMixList,
    getPrefixList,
    getRaceData, getRelationshipData, getReportTypeData, getSampleTypesList, getSampleTypesWithCollectionDevices,
    getSuffixList
} from "../../../store/actions/staticData.action";
import TooltipComponent from "../../../shared/components/tooltip/tooltipComponent";
import ChipComponent from "../../../shared/components/chip/ChipComponent";
import EditIcon from '@mui/icons-material/Edit';

interface SystemSettingsTableWrapperComponentProps {
    module: string;
    feature: string;
    isPaginated?: boolean;
    featureTitle: string;
}

const MODULE_NAME = "SystemSettingsTableWrapperComponent";

const ZipCodeValidationSchema = Yup.object().shape({
    zipcode: Yup.string().required('Zip Code is required')
        .matches(/^[1-9]\d{4}$/, {message: "Please Enter a Valid Zipcode"})
        .max(5, "Zipcode should be 5 digits")
        .min(5, "Zipcode should be 5 digits")
        .nullable(),
    state: Yup.string().required('State is required').nullable(),
    city: Yup.string().required('City is required').nullable(),
    county: Yup.string().required('Country is required').nullable(),
    country: Yup.string().required('Country is required').nullable(),
});

interface ZipcodeFormSchema {
    zipcode: string,
    state: string,
    city: string,
    county: string
    country: string,
}

const ZipcodeFormInitialValues: ZipcodeFormSchema = {
    zipcode: '',
    state: '',
    city: '',
    county: '',
    country: 'USA', // set default country to USA
}

const generalValidationSchema = Yup.object({
    type: Yup.string().required('Required').nullable(),
})

interface generalFormSchema {
    type: string,
}

const generalFormInitailValues: generalFormSchema = {
    type: ''
}

const SystemSettingsGeneralValidationSchema = Yup.object({
    typeArray: Yup.array(generalValidationSchema).min(1).required('Required'),
})

interface SystemSettingsGeneralFormSchema {
    typeArray: generalFormSchema[]
}


const SystemSettingsGeneralInitialValues: SystemSettingsGeneralFormSchema = {
    typeArray: [generalFormInitailValues]
}

interface IDrawerConfig {
    isOpen: boolean;
    featureType: string;
    featureTitle: string;
    id?: string | undefined | number;
}

const initialDrawerConfig: IDrawerConfig = {
    isOpen: false,
    featureType: "",
    featureTitle: "",
    id: undefined
}

const isDuplicateStringInArray = (featuresToAdd: string[], existingSampleTypes: string[] = []) => {
    const featureCount: any = {};
    for (const feature of existingSampleTypes) {
        const featureCode = feature?.toLowerCase();
        featureCount[featureCode] = (featureCount[featureCode] || 0) + 1;
    }
    for (const feature of featuresToAdd) {
        featureCount[feature.toLowerCase()] = (featureCount[feature.toLowerCase()] || 0) + 1;
    }
    return Object.values(featureCount).some((count: any) => count > 1);
}

const SystemSettingsTableWrapperComponent = (props: SystemSettingsTableWrapperComponentProps) => {
    let {module, feature, isPaginated, featureTitle} = props;
    const [initialFormValues, setInitialFormValues] = useState<SystemSettingsGeneralFormSchema | ZipcodeFormSchema>(feature === 'address' ? ZipcodeFormInitialValues : SystemSettingsGeneralInitialValues)

    const [filterData, setFilterData] = useState<any>([]);
    const dispatch = useDispatch();

    const [featureData, setFeatureData] = useState<any>([]);
    const [drawerConfig, setDrawerConfig] = useState<IDrawerConfig>(initialDrawerConfig);
    const [isFeatureDataLoaded, setIsFeatureDataLoaded] = useState<boolean>(false);
    const [isSelectAll, setIsSelectAll] = useState<boolean>(false);
    const [isIndeterminate, setIsIndeterminate] = useState<boolean>(false);
    const [selectedTypesList, setSelectedTypesList] = useState<any[]>([]);

    const handleSelectDeselectAllTypes = useCallback((isChecked: boolean) => {
        let updatedTypesList = _.cloneDeep(selectedTypesList);
        if (isChecked) {
            updatedTypesList.push(...featureData);
        } else {
            updatedTypesList = [];
        }
        setSelectedTypesList(updatedTypesList);
    }, [selectedTypesList, featureData]);

    const handleSelectDeselectType = useCallback((isChecked: boolean, type: any) => {
        const updatedTypesList = _.cloneDeep(selectedTypesList);
        if (feature === 'address') {
            if (isChecked) {
                updatedTypesList.push(type);
            } else {
                const zipcode = type.zipcode;
                updatedTypesList.splice(updatedTypesList.findIndex(item => item.zipcode === zipcode), 1);
            }
        } else if (feature === "sampleTypes") {
            if (isChecked) {
                updatedTypesList.push(type);
            } else {
                const code = type.sampleType;
                updatedTypesList.splice(updatedTypesList.findIndex(item => item.sampleType === code), 1);
            }
        } else {
            if (isChecked) {
                updatedTypesList.push(type);
            } else {
                const code = type.code;
                updatedTypesList.splice(updatedTypesList.findIndex(item => item.code === code), 1);
            }
        }
        setSelectedTypesList(updatedTypesList);
    }, [selectedTypesList, feature]);

    useEffect(() => {
        if (selectedTypesList.length > 0) {
            if (feature === 'address') {
                if (featureData.map((type: any) => type.zipcode).every((zipcode: any) => selectedTypesList.map((type: any) => type.zipcode).includes(zipcode))) {
                    setIsSelectAll(true);
                    setIsIndeterminate(false);
                } else {
                    setIsSelectAll(false);
                    setIsIndeterminate(true);
                }
            } else if (feature === 'sampleTypes') {
                if (featureData.map((type: any) => type.sampleType).every((type: any) => selectedTypesList.map((type: any) => type.sampleType).includes(type))) {
                    setIsSelectAll(true);
                    setIsIndeterminate(false);
                } else {
                    setIsSelectAll(false);
                    setIsIndeterminate(true);
                }
            } else {
                if (featureData.map((type: any) => type.code).every((type: any) => selectedTypesList.map((type: any) => type.code).includes(type))) {
                    setIsSelectAll(true);
                    setIsIndeterminate(false);
                } else {
                    setIsSelectAll(false);
                    setIsIndeterminate(true);
                }
            }
        } else {
            setIsIndeterminate(false);
            setIsSelectAll(false)
        }
    }, [selectedTypesList, feature, featureData]);

    useEffect(() => {
        setSelectedTypesList([])
    }, [feature, module])
    useEffect(() => {
        setInitialFormValues(feature === 'address' ? ZipcodeFormInitialValues : SystemSettingsGeneralInitialValues)
    }, [feature]);

    const ZipCodeColumns: ITableColumn[] = useMemo<any>(() => [
        {
            id: 'select',
            label:
                <CheckboxComponent
                    name={"sample_select_all"}
                    size={"small"}
                    disableRipple={true}
                    checked={isSelectAll}
                    indeterminate={isIndeterminate}
                    onUpdate={(isChecked: any) => {

                        handleSelectDeselectAllTypes(isChecked)
                    }}
                />,
            hideInColumnPreferences: true,
            isDefaultColumn: true,
            fixed: "left",
            align: "center",
            width: '70',
            content: (row: any) => (
                <>
                    <CheckboxComponent
                        name={"type_select"}
                        size={"small"}
                        disableRipple={true}
                        checked={selectedTypesList.findIndex((type: any) => type.zipcode === row.zipcode) > -1}
                        onUpdate={(isChecked: boolean) => {
                            handleSelectDeselectType(isChecked, row);
                        }}/>
                </>
            )
        },
        {
            id: 'zipcode',
            label: 'Zip Code',
            minWidth: 170,
        },
        {
            id: 'city',
            label: 'City',
            minWidth: 170,

        },
        // {
        //     id: 'county',
        //     label: 'Country',
        //     minWidth: 170,
        // },
        {
            id: 'state',
            label: 'State',
            minWidth: 170,
            fixed: "right",
        }
        // {
        //     id: 'country',
        //     label: 'Country',
        //     minWidth: 170,
        //     fixed: "right",
        // }
    ], [
        handleSelectDeselectAllTypes, handleSelectDeselectType, isIndeterminate,
        isSelectAll, selectedTypesList
    ]);

    useEffect(() => {
        setIsFeatureDataLoaded(false);
        if (feature && module) {
            setFilterData({
                'module': module,
                'feature': feature
            })
        }
    }, [feature, module])

    const GeneralColumns: ITableColumn[] = useMemo<any>(() => [
        {
            id: 'select',
            label:
                <CheckboxComponent
                    name={"sample_select_all"}
                    size={"small"}
                    disableRipple={true}
                    checked={isSelectAll}
                    indeterminate={isIndeterminate}
                    onUpdate={handleSelectDeselectAllTypes}
                />,
            hideInColumnPreferences: true,
            isDefaultColumn: true,
            fixed: "left",
            width: '50',
            content: (row: any) => (
                <>
                    <CheckboxComponent
                        name={"sample_select"}
                        size={"small"}
                        disableRipple={true}
                        checked={selectedTypesList.findIndex((type: any) => type.code === row.code) > -1}
                        onUpdate={(isChecked: boolean) => {
                            handleSelectDeselectType(isChecked, row);
                        }
                        }/>
                </>
            )

        },
        {
            id: 'type',
            label: `${commonService.capitalizeFirstLetterOfEachWord(featureTitle)}`,
            minWidth: 170,
            content: (row: any) => (
                <>
                    {commonService.capitalizeFirstLetterOfEachWord(row.title)}
                </>
            )
        }
    ], [
        handleSelectDeselectAllTypes, handleSelectDeselectType, isIndeterminate, isSelectAll, selectedTypesList,
        featureTitle
    ]);

    const SampleTypeColumns: ITableColumn[] = useMemo<any>(() => [
        {
            id: 'select',
            label:
                <CheckboxComponent
                    name={"sample_select_all"}
                    size={"small"}
                    disableRipple={true}
                    checked={isSelectAll}
                    indeterminate={isIndeterminate}
                    onUpdate={handleSelectDeselectAllTypes}
                />,
            hideInColumnPreferences: true,
            isDefaultColumn: true,
            fixed: "left",
            width: '50',
            content: (row: any) => (
                <>
                    <CheckboxComponent
                        name={"sample_select"}
                        size={"small"}
                        disableRipple={true}
                        checked={selectedTypesList.findIndex((type: any) => type.sampleType === row.sampleType) > -1}
                        onUpdate={(isChecked: boolean) => {
                            handleSelectDeselectType(isChecked, row);
                        }
                        }/>
                </>
            )

        },
        {
            id: 'type',
            label: `${commonService.capitalizeFirstLetterOfEachWord(featureTitle)}`,
            minWidth: 170,
            content: (row: any) => (
                <>
                    {commonService.capitalizeFirstLetterOfEachWord(row.sampleType)}
                </>
            )
        },
        {
            id: 'sampleCollectionDevice',
            label: "Sample Collection Device",
            minWidth: 400,
            content: (row: any) => {
                const value = row?.sampleCollectionDeviceName || [];
                const chips = value?.map((device: any) => <ChipComponent
                    label={commonService.capitalizeFirstLetterOfEachWord(device?.title)}/>);
                return <>
                    {value?.length ? (value?.length > 4 ?
                        <TooltipComponent tooltip={chips}>
                            <div className={'d-inline-flex'}>{chips}</div>
                        </TooltipComponent> : chips) : "-"}
                </>
            }
        },
        {
            id: 'action',
            label: '',
            minWidth: 100,
            content: (row: any) => {
                return (
                    <ButtonComponent
                        startIcon={<EditIcon/>}
                        color={"secondary"}
                        variant={'outlined'}
                        handleClick={() => {
                            setDrawerConfig({
                                isOpen: true,
                                featureType: "sampleCollectionDeviceNames",
                                featureTitle: "Collection Device",
                                id: row?.id
                            })
                            row?.sampleCollectionDeviceName && row?.sampleCollectionDeviceName?.length > 0 && setInitialFormValues({
                                typeArray: row?.sampleCollectionDeviceName?.map((deviceName: any) => {
                                    return {
                                        type: deviceName.code
                                    }
                                })
                            })
                        }}
                    >
                        Edit Collection Device
                    </ButtonComponent>
                )
            }
        }
    ], [
        handleSelectDeselectAllTypes, handleSelectDeselectType, isIndeterminate, isSelectAll, selectedTypesList,
        featureTitle
    ]);

    const getTableColumns = useCallback((feature: any) => {
        switch (feature) {
            case 'address':
                return ZipCodeColumns;
            case 'sampleTypes':
                return SampleTypeColumns;
            default:
                return GeneralColumns;
        }
    }, [GeneralColumns, SampleTypeColumns, ZipCodeColumns])

    const handleCloseDrawer = useCallback(() => {
        setDrawerConfig(initialDrawerConfig);
    }, [])

    const handleFeatureDataChange = useCallback((feature: any) => {
        switch (feature) {
            case 'facilityType':
                dispatch(getFacilityTypes())
                break;
            case 'race':
                dispatch(getRaceData())
                break;
            case 'ethnicity':
                dispatch(getEthnicityData())
                break;
            // case 'gender':
            //     dispatch()
            case 'labType':
                dispatch(getLabListLite())
                break;
            case 'locationType':
                dispatch(getLocationListLite())
                break;
            // case 'preferredDeliveryReport':
            //     break;
            // case 'pickupMethod':
            //     break;
            // case 'pickupSchedule':
            //     break;
            case 'payorMix':
                dispatch(getPayorMixList())
                break;
            case 'sampleTypes':
                dispatch(getSampleTypesList())
                dispatch(getSampleTypesWithCollectionDevices())
                break;
            case 'reportTypes':
                dispatch(getReportTypeData())
                break;
            case 'equipmentType':
                dispatch(getEquipmentTypeList())
                break;
            case 'insurers':
                dispatch(getInsuranceCompanyData())
                break;
            case 'relationships':
                dispatch(getRelationshipData())
                break;
            // case 'resonsForSampleRejection':
            // case 'reasonForOrderCancellation':
            case 'prefix':
                dispatch(getPrefixList())
                break;
            case 'suffix':
                dispatch(getSuffixList())
                break;
            case 'maritalStatus':
                dispatch(getMaritalStatusList())
                break;
        }
    }, [dispatch])

    const onFormSubmit = useCallback((values: any, {setSubmitting, setErrors, validateForm}: FormikHelpers<any>) => {
        setSubmitting(true);
        if (feature === 'address') {
            const payload: any = {
                'module': module,
                'feature': feature,
                'value': {}
            };
            payload['value'] = values;
            // We don't need to send the typeArray to the backend, it causes server crash
            // Check for typeArray and if it's there then delete it from the payload
            if (payload?.value?.typeArray) {
                delete payload?.value?.typeArray
            }
            commonService._systemSettings.addModuleFeatureData(payload)
                .then((res: any) => {
                        setSubmitting(false);
                        CommonService._communications.TableWrapperRefreshSubject.next({
                            moduleName: MODULE_NAME
                        });
                        handleCloseDrawer()
                    }
                ).catch((err: any) => {
                setSubmitting(false);
                commonService._alert.showToast(err.error || 'Something went wrong', 'error');
            });
        } else if (feature === 'sampleTypes') {
            const featureType = drawerConfig.featureType;
            const payload: any = {
                'module': module,
                'feature': featureType,
                'value': {}
            };
            payload['value'] = values?.typeArray?.map((value: any) => {
                return {'title': value.type?.toLowerCase(), 'code': value.type?.toLowerCase()}
            })
            const selectedFeatures = values?.typeArray?.map((value: any) => value.type);
            const existingSampleTypes = featureData?.map((feature: any) => feature?.sampleType?.toLowerCase());
            //if feature type is collection device name then we need to compare the duplicates with formik selected fields, if it's sample types then need to compare with existing features in table.
            const isDuplicateFeatureInArray = isDuplicateStringInArray(selectedFeatures, featureType === "sampleTypes" ? existingSampleTypes : undefined);
            if (featureType === "sampleCollectionDeviceNames") {
                payload['sampleTypeId'] = drawerConfig.id;
            }
            if (isDuplicateFeatureInArray) {
                commonService._alert.showToast(`Duplicate ${drawerConfig.featureTitle} found`, "error")
                setSubmitting(false);
                return;
            }
            commonService._systemSettings.addModuleFeatureData(payload)
                .then((res: any) => {
                        setSubmitting(false);
                        CommonService._communications.TableWrapperRefreshSubject.next({
                            moduleName: MODULE_NAME
                        });
                        handleFeatureDataChange(feature);
                        handleCloseDrawer()
                    }
                ).catch((err: any) => {
                setSubmitting(false);
                commonService.handleErrors(err, 'addModuleFeatureData')
            });
        } else {
            const payload = {
                'module': module,
                'feature': feature,
                'value': {}
            };
            payload['value'] = values?.typeArray?.map((value: any) => {
                return {'title': value.type, 'code': value.type}
            })
            commonService._systemSettings.addModuleFeatureData(payload)
                .then((res: any) => {
                        setSubmitting(false);
                        CommonService._communications.TableWrapperRefreshSubject.next({
                            moduleName: MODULE_NAME
                        });
                        handleFeatureDataChange(feature);
                        handleCloseDrawer()
                    }
                ).catch((err: any) => {
                setSubmitting(false);
                commonService.handleErrors(err, 'addModuleFeatureData')
            });
        }

    }, [feature, module, handleCloseDrawer, drawerConfig.featureType, drawerConfig.id, drawerConfig.featureTitle, featureData, handleFeatureDataChange]);

    const handleDeleteTypes = useCallback(() => {
        const payload: any = {
            'module': module,
            'feature': feature,
            'value': []
        }
        if (feature === 'address') {
            selectedTypesList.map((selectedType: any) =>
                payload['value'].push(selectedType?.zipcode))

        } else if (feature === 'sampleTypes') {
            selectedTypesList.map((selectedType: any) =>
                payload['value'].push(selectedType?.sampleType)
            )
        } else {
            selectedTypesList.map((selectedType: any) =>
                payload['value'].push(selectedType?.code)
            )
        }
        commonService._systemSettings.deleteModuleFeatureData(payload)
            .then((res: any) => {
                CommonService._communications.TableWrapperRefreshSubject.next({
                    moduleName: MODULE_NAME
                })
                handleFeatureDataChange(feature);
                setSelectedTypesList([]);
            })
            .catch(
                (err: any) => {
                    commonService.handleErrors(err, 'deleteModuleFeatureData')

                })

    }, [selectedTypesList, feature, module, handleFeatureDataChange])
    const handleDeleteTypesConfirmation = useCallback(() => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: 'Are you sure you want to delete this  feature ?',
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.DeleteImage}`,
            direction: "up",
            yes: {
                text: "Confirm",
                color: "primary"
            },
            no: {
                text: "Cancel",
                color: "primary"
            }
        })
            .then(() => {
                handleDeleteTypes()
            })
            .catch(() => {
            })

    }, [handleDeleteTypes])

    const handleSearch = useCallback((value: string) => {
        setFilterData((prevState: any) => ({
            ...prevState,
            search: value
        }));
    }, []);

    return (
        <div className={'system-settings-table-wrapper-component'}>
            {
                (!module || !feature) &&
                <div className={'no-data'}>Please select a module and feature</div>
            }
            {
                module && feature &&
                <div className={'table-wrapper'}>
                    <div className={'table-header'}>
                        {/*{*/}
                        {/*    isFeatureDataLoadingFailed &&*/}
                        {/*    <StatusCardComponent title={"Feature details failed to load"}/>*/}
                        {/*}*/}
                        {
                            isFeatureDataLoaded &&
                            <div className={'feature-data-table-header'}>
                                {feature === 'address' &&
                                    <div className="page-search">
                                        <SearchComponent
                                            label={"Search ZIP Code, City, County or State"}
                                            id="search_input"
                                            placeholder={"Search ZIP Code, City, County or State"}
                                            value={filterData.search}
                                            onChange={handleSearch}
                                        />
                                    </div>
                                }
                                <div className={'feature-action-buttons-wrapper'}>

                                    <div className={'feature-action-button'}>
                                        <ButtonComponent
                                            variant={"outlined"}
                                            color={"secondary"}
                                            startIcon={<DeleteIcon/>}
                                            disabled={selectedTypesList.length === 0}
                                            handleClick={() => {
                                                handleDeleteTypesConfirmation()
                                            }}
                                        >
                                            Delete
                                        </ButtonComponent>
                                    </div>
                                    <div className={'feature-action-button'}>
                                        <ButtonComponent
                                            variant={"contained"}
                                            color={"primary"}
                                            startIcon={<AddIcon/>}
                                            handleClick={() => {
                                                setDrawerConfig({
                                                    isOpen: true,
                                                    featureType: feature,
                                                    featureTitle: featureTitle,
                                                })
                                            }}
                                        >
                                            Add {commonService.capitalizeFirstLetterOfEachWord(featureTitle)}
                                        </ButtonComponent>
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                    <div className={`table-list-container ${!isFeatureDataLoaded && "display-none"}`}>
                        {
                            module && feature &&
                            <TableWrapperV2Component
                                isPaginated={isPaginated}
                                columns={getTableColumns(feature)}
                                url={APIConfig.GET_MODULE_FEATURE_DATA.URL}
                                method={APIConfig.GET_MODULE_FEATURE_DATA.METHOD}
                                isColumnFilterVisible={false}
                                onDataLoad={(data: any) => {
                                    setFeatureData(data);
                                    setIsFeatureDataLoaded(true);
                                }}
                                extraPayload={filterData}
                                moduleName={MODULE_NAME}
                            />
                        }

                    </div>
                </div>

            }
            <DrawerComponent
                open={drawerConfig.isOpen}
                onClose={() => {
                    handleCloseDrawer()
                }
                }
            >
                <div className={'system-settings-drawer-content screen t-form-screen'}>

                    {feature === 'address' ?
                        <div className="t-form t-form-drawer">
                            <Formik
                                initialValues={initialFormValues as ZipcodeFormSchema}
                                onSubmit={onFormSubmit}
                                validationSchema={ZipCodeValidationSchema}
                            >
                                {({isSubmitting, values, isValid, touched, setFieldValue, validateForm, errors}) => {
                                    // eslint-disable-next-line react-hooks/rules-of-hooks
                                    useEffect(() => {
                                        validateForm();
                                    }, [validateForm, values]);
                                    return (
                                        <Form className={'system-settings-add-form-wrapper'} noValidate={true}>
                                            <div className="drawer-sub-header horizontal-vertical-center-align">
                                                <HeadingComponent
                                                    title={"Add " + commonService.capitalizeFirstLetterOfEachWord(feature)}/>
                                                {/*<div className="deselect-content"*/}
                                                {/*     onClick={()=>{handleClearColumnFilter()}}>*/}
                                                {/*    Deselect All*/}
                                                {/*</div>*/}
                                            </div>
                                            <div className="t-form-controls-wrapper">
                                                <Field name={'zipcode'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Enter Zip Code'}
                                                                placeholder={'Enter Zip Code'}
                                                                type={"text"}
                                                                maxLength={5}
                                                                required={true}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                                <Field name={'city'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Enter City'}
                                                                placeholder={'Enter City'}
                                                                type={"text"}
                                                                required={true}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                                <Field name={'county'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Enter County'}
                                                                placeholder={'Enter County'}
                                                                type={"text"}
                                                                required={true}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                                <Field name={'state'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Enter State'}
                                                                placeholder={'Enter State'}
                                                                type={"text"}
                                                                required={true}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                                <Field name={'country'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Enter Country'}
                                                                placeholder={'Enter Country'}
                                                                type={"text"}
                                                                required={true}
                                                                readOnly={true}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div
                                                className={'drawer-actions mrg-top-20 form-action-section'}>
                                                <ButtonComponent
                                                    variant={"contained"}
                                                    color={"primary"}
                                                    fullWidth={true}
                                                    type={"submit"}
                                                    disabled={isSubmitting || !isValid}
                                                >
                                                    Add
                                                </ButtonComponent>
                                            </div>
                                        </Form>
                                    )
                                }
                                }
                            </Formik>
                        </div> :
                        <div className="t-form t-form-drawer">
                            <Formik
                                initialValues={initialFormValues as SystemSettingsGeneralFormSchema}
                                onSubmit={onFormSubmit}
                                validationSchema={SystemSettingsGeneralValidationSchema}
                            >
                                {({isSubmitting, values, isValid, touched, setFieldValue, validateForm, errors}) => {
                                    // eslint-disable-next-line react-hooks/rules-of-hooks
                                    useEffect(() => {
                                        validateForm();
                                    }, [validateForm, values]);
                                    return (
                                        <Form className={'system-settings-add-form-wrapper'} noValidate={true}>
                                            <div className="drawer-sub-header horizontal-vertical-center-align">
                                                <HeadingComponent
                                                    title={`${drawerConfig.featureType === "sampleCollectionDeviceNames" ? "Edit" : "Add"} ${drawerConfig.featureTitle}`}/>
                                                {/*<div className="deselect-content"*/}
                                                {/*     onClick={()=>{handleClearColumnFilter()}}>*/}
                                                {/*    Deselect All*/}
                                                {/*</div>*/}
                                            </div>
                                            <div className="t-form-controls-wrapper">
                                                <FieldArray name={'typeArray'}
                                                            render={(arrayHelpers: any) => (
                                                                <>
                                                                    {
                                                                        values?.typeArray?.map((value: any, index: number) => {
                                                                            return (
                                                                                <div key={index}>
                                                                                    <div
                                                                                        className={'type-field-with-delete-icon-wrapper'}>
                                                                                        <Field
                                                                                            name={`typeArray[${index}].type`}>
                                                                                            {
                                                                                                (field: FieldProps) => (
                                                                                                    <FormikInputComponent
                                                                                                        label={`Enter ${commonService.capitalizeFirstLetterOfEachWord(drawerConfig.featureTitle)}`}
                                                                                                        placeholder={`Enter ${commonService.capitalizeFirstLetterOfEachWord(drawerConfig.featureTitle)}`}
                                                                                                        type={"text"}
                                                                                                        required={true}
                                                                                                        formikField={field}
                                                                                                    />
                                                                                                )
                                                                                            }
                                                                                        </Field>
                                                                                        <div
                                                                                            className={'delete-type-field'}>
                                                                                            <ButtonComponent
                                                                                                type="button"
                                                                                                color={"secondary"}
                                                                                                variant={"icon"}
                                                                                                fullWidth={false}
                                                                                                id="delete_icd_code_btn"
                                                                                                handleClick={() => {
                                                                                                    arrayHelpers.remove(index)
                                                                                                    if (index === 0 && values?.typeArray?.length === 1) {
                                                                                                        arrayHelpers.push(generalFormInitailValues)
                                                                                                    }
                                                                                                }}
                                                                                            >
                                                                                                <DeleteIcon/>
                                                                                            </ButtonComponent>
                                                                                        </div>
                                                                                    </div>
                                                                                    {
                                                                                        index === values?.typeArray?.length - 1 &&
                                                                                        <ButtonComponent
                                                                                            type="button"
                                                                                            color={"secondary"}
                                                                                            variant={"text"}
                                                                                            fullWidth={false}
                                                                                            id="add_another_icd_code_btn"
                                                                                            startIcon={<AddIcon/>}
                                                                                            disabled={!isValid}
                                                                                            handleClick={() => arrayHelpers.push(generalFormInitailValues)}
                                                                                        >
                                                                                            Add More
                                                                                        </ButtonComponent>
                                                                                    }
                                                                                </div>
                                                                            )
                                                                        })
                                                                    }
                                                                </>
                                                            )}
                                                />
                                            </div>
                                            <div
                                                className={'drawer-actions mrg-top-20 form-action-section'}>
                                                <ButtonComponent
                                                    variant={"contained"}
                                                    color={"primary"}
                                                    fullWidth={true}
                                                    type={"submit"}
                                                    disabled={isSubmitting || !isValid}
                                                >
                                                    {drawerConfig.featureType === "sampleCollectionDeviceNames" ? "Save" : "Add"}
                                                </ButtonComponent>
                                            </div>
                                        </Form>
                                    )
                                }}
                            </Formik>
                        </div>}
                </div>
            </DrawerComponent>
        </div>
    )
}

export default SystemSettingsTableWrapperComponent;
